import { ArrowLeft, Maximize2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import { Copy, Check } from 'lucide-react';
import { apiFetch } from '../api/client';
import { WaveformViewer, WaveformJumpTo } from './WaveformViewer';

interface PointData {
  V: number;
  H: number;
  A: number;
}

interface Thresholds {
  yellow: number;
  red: number;
}

interface Point {
  id: string;
  name: string;
  velocity: PointData;
  acceleration: PointData;
  envelope: PointData;
  temperature: PointData;
  velocityThresholds?: Thresholds;
  accelerationThresholds?: Thresholds;
  envelopeThresholds?: Thresholds;
  temperatureThresholds?: Thresholds;
  status: 'low' | 'medium' | 'high';
  machineId?: string;
  axisIds?: number[];
}

interface PointDetailProps {
  point: Point;
  onBack: () => void;
  onThresholdsUpdate?: (pointId: string, metric: string, thresholds: Thresholds) => void;
  initialAxisNum?: number;
}

const AXIS_COLORS: Record<string, string> = { V: '#8884d8', H: '#82ca9d', A: '#FF0000' };

// Generate historical data for a point metric with V, H, A
const generatePointHistoricalData = (pointData: PointData, timeRange: number) => {
  let timeStamps: string[];
  switch (timeRange) {
    case 0: timeStamps = ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '24:00']; break;
    case 1: timeStamps = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']; break;
    case 2: timeStamps = ['Week 1', 'Week 2', 'Week 3', 'Week 4']; break;
    case 3: timeStamps = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun']; break;
    default: timeStamps = ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '24:00'];
  }
  return timeStamps.map((time, index) => {
    const multiplier = 0.8 + (Math.sin(index * 0.5) * 0.2) + (index % 2 === 0 ? 0.1 : 0);
    return {
      time,
      V: pointData.V * (multiplier + 0.2),
      H: pointData.H * (multiplier + 0.15),
      A: pointData.A * (multiplier + 0.25),
    };
  });
};

const getTimeRangeLabel = (value: number) => {
  switch (value) {
    case 0: return 'Hours';
    case 1: return 'Days';
    case 2: return 'Weeks';
    case 3: return 'Months';
    default: return 'Hours';
  }
};

function applyFloor(data: any[], floor: number): any[] {
  if (floor <= 0) return data;
  return data.map(entry => {
    const out: any = { time: entry.time };
    for (const [k, v] of Object.entries(entry)) {
      if (k === 'time') continue;
      out[k] = typeof v === 'number' && v < floor ? null : v;
    }
    return out;
  });
}

function FloorSlider({ floor, setFloor, yMax }: { floor: number; setFloor: (v: number) => void; yMax: number }) {
  const step = yMax > 0 ? yMax / 200 : 0.001;
  return (
    <div className="flex flex-col items-center justify-between shrink-0" style={{ height: 300, width: 28, paddingTop: 6, paddingBottom: 4 }}>
      <span className="font-mono tabular-nums text-center" style={{ fontSize: 9, color: '#64748b', minHeight: 12, lineHeight: '12px', writingMode: 'horizontal-tb' }}>
        {floor > 0 ? floor.toFixed(3) : ''}
      </span>
      <input
        type="range" min={0} max={yMax || 1} step={step}
        value={Math.min(floor, yMax || 1)}
        onChange={e => setFloor(Number(e.target.value))}
        style={{ writingMode: 'vertical-lr', direction: 'rtl', height: 240, width: 18, cursor: 'pointer', accentColor: '#64748b', flexShrink: 0 }}
        title="Drag to filter: hides data below this value"
      />
      <span style={{ fontSize: 9, color: '#9ca3af' }}>0</span>
    </div>
  );
}

function calcYMax(data: any[], threshMod?: number | null): number {
  const vals = data.flatMap(d =>
    Object.entries(d)
      .filter(([k]) => k !== 'time')
      .map(([, v]) => v as number)
      .filter(v => v != null && !isNaN(v))
  );
  const dataMax = vals.length > 0 ? Math.max(...vals) : 0;
  const top = Math.max(dataMax, threshMod ?? 0);
  return top > 0 ? parseFloat((top * 1.2).toFixed(4)) : 1;
}

// Build Map<bucket, {file_id, measured_at}> for waveform dots from raw trend rows.
function buildWfMarkers(rows: any[], pointName: string): Map<string, { file_id: number; measured_at: string }> {
  const result = new Map<string, { file_id: number; measured_at: string }>();
  for (const r of rows) {
    if (!r.file_id || r.reason !== 2 || r.point_name !== pointName) continue;
    const bucket = (r.measured_at || '').substring(0, 10).replace(/\//g, '-');
    if (!result.has(bucket)) result.set(bucket, { file_id: r.file_id, measured_at: r.measured_at });
  }
  return result;
}

// Find nearest time bucket that has a waveform, searching outward from activeIdx.
function findNearestWfPt(
  data: any[],
  idx: number,
  markersMap: Map<string, { file_id: number; measured_at: string }> | undefined,
  maxSearch = 10
): { time: string; measured_at: string } | null {
  if (!markersMap) return null;
  for (let d = 0; d <= maxSearch; d++) {
    for (const off of d === 0 ? [0] : [d, -d]) {
      const i = idx + off;
      if (i < 0 || i >= data.length) continue;
      const time = data[i]?.time;
      if (time && markersMap.has(time)) return { time, measured_at: markersMap.get(time)!.measured_at };
    }
  }
  return null;
}

// Format 'YYYY/MM/DD HH:MM:SS' → "Apr 21 9:26 PM — click to load"
function fmtWfLabel(measured_at: string): string {
  const [dp, tp = '00:00:00'] = measured_at.split(' ');
  const [, mo, dy] = dp.split('/');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  let h = parseInt(tp.split(':')[0]);
  const mn = tp.split(':')[1];
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${months[parseInt(mo)-1]} ${parseInt(dy)} ${h}:${mn} ${ap} — click to load`;
}

// Chart Container with Copy functionality
function ChartContainer({ children, title, onExpand }: { children: React.ReactNode; title: string; onExpand?: () => void }) {
  const chartRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  const handleCopyChart = async () => {
    if (!chartRef.current) return;
    try {
      const svgElement = chartRef.current.querySelector('svg');
      if (!svgElement) return;
      const rect = svgElement.getBoundingClientRect();
      const canvas = document.createElement('canvas');
      canvas.width = rect.width * 2;
      canvas.height = rect.height * 2;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.scale(2, 2);
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, rect.width, rect.height);
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();
      img.onload = async () => {
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
        URL.revokeObjectURL(url);
        canvas.toBlob(async (blob) => {
          if (!blob) return;
          try {
            await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          } catch {}
        });
      };
      img.src = url;
    } catch {}
  };

  return (
    <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-gray-900">{title}</h3>
        <div className="flex items-center gap-2">
          {onExpand && (
            <button onClick={onExpand} className="p-2 hover:bg-gray-100 rounded-lg transition-all group" title="Expand chart">
              <Maximize2 className="w-4 h-4 text-gray-600 group-hover:text-gray-900" />
            </button>
          )}
          <button onClick={handleCopyChart} className="p-2 hover:bg-gray-100 rounded-lg transition-all group" title="Copy chart to clipboard">
            {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-600 group-hover:text-gray-900" />}
          </button>
        </div>
      </div>
      <div ref={chartRef}>{children}</div>
    </div>
  );
}

export function PointDetail({ point, onBack, onThresholdsUpdate, initialAxisNum }: PointDetailProps) {
  const [velocityTimeRange, setVelocityTimeRange] = useState(2);
  const [accelerationTimeRange, setAccelerationTimeRange] = useState(2);
  const [envelopeTimeRange, setEnvelopeTimeRange] = useState(2);
  const [temperatureTimeRange, setTemperatureTimeRange] = useState(2);

  const [velocityThresholds, setVelocityThresholds] = useState(point.velocityThresholds ?? { yellow: 0, red: 0 });
  const [accelerationThresholds, setAccelerationThresholds] = useState(point.accelerationThresholds ?? { yellow: 0, red: 0 });
  const [envelopeThresholds, setEnvelopeThresholds] = useState(point.envelopeThresholds ?? { yellow: 0, red: 0 });
  const [temperatureThresholds, setTemperatureThresholds] = useState(point.temperatureThresholds ?? { yellow: 0, red: 0 });

  const [velocityDirty, setVelocityDirty] = useState(false);
  const [accelerationDirty, setAccelerationDirty] = useState(false);
  const [envelopeDirty, setEnvelopeDirty] = useState(false);
  const [savingMetric, setSavingMetric] = useState<string | null>(null);

  const [velFloor,   setVelFloor]   = useState(0);
  const [accelFloor, setAccelFloor] = useState(0);
  const [envFloor,   setEnvFloor]   = useState(0);
  const [tempFloor,  setTempFloor]  = useState(0);

  type AxisRow = { time: string; V: number | null; H: number | null; A: number | null };
  const [trendData, setTrendData] = useState<Record<number, AxisRow[]>>({ 0: [], 2: [], 6: [] });
  const [sensorTempData, setSensorTempData] = useState<{ time: string; temp: number }[]>([]);

  // Waveform state
  const [wfMarkersMap, setWfMarkersMap] = useState<Record<number, Map<string, { file_id: number; measured_at: string }>>>({});
  const [waveformJumpTo, setWaveformJumpTo] = useState<WaveformJumpTo | null>(null);
  const [wfHover, setWfHover] = useState<{ unitCode: number; time: string; measured_at: string } | null>(null);
  const wfHoverRef = useRef<{ unitCode: number; time: string; measured_at: string } | null>(null);
  wfHoverRef.current = wfHover;

  const timeRangeToDays = (tr: number) => [1, 7, 30, 180][tr] ?? 30;
  const MM_TO_IN = 1 / 25.4;

  const buildAxisRows = (rows: any[], unitCode: number): AxisRow[] => {
    const scale = unitCode === 2 ? MM_TO_IN : 1;
    const buckets = new Map<string, { V: number[]; H: number[]; A: number[] }>();
    for (const r of rows.filter(r => r.point_name === point.name)) {
      if (r.value == null) continue;
      const day = (r.measured_at || '').substring(0, 10).replace(/\//g, '-');
      if (!buckets.has(day)) buckets.set(day, { V: [], H: [], A: [] });
      const b = buckets.get(day)!;
      const v = r.value * scale;
      if      (r.axis_num === 2) b.V.push(v);
      else if (r.axis_num === 1) b.H.push(v);
      else if (r.axis_num === 3) b.A.push(v);
    }
    const decimals = unitCode === 2 ? 4 : 3;
    const peak = (arr: number[]): number | null =>
      arr.length ? parseFloat(Math.max(...arr).toFixed(decimals)) : null;
    return Array.from(buckets.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, b]) => ({ time: day, V: peak(b.V), H: peak(b.H), A: peak(b.A) }));
  };

  useEffect(() => {
    if (!point.machineId) return;
    const days = timeRangeToDays(velocityTimeRange);
    apiFetch<any[]>(`/machines/${point.machineId}/trend?unit_code=2&days=${days}`)
      .then(rows => {
        setTrendData(prev => ({ ...prev, 2: buildAxisRows(rows, 2) }));
        setWfMarkersMap(prev => ({ ...prev, 2: buildWfMarkers(rows, point.name) }));
      }).catch(() => {});
  }, [point.machineId, point.name, velocityTimeRange]);

  useEffect(() => {
    if (!point.machineId) return;
    const days = timeRangeToDays(accelerationTimeRange);
    apiFetch<any[]>(`/machines/${point.machineId}/trend?unit_code=0&days=${days}`)
      .then(rows => {
        setTrendData(prev => ({ ...prev, 0: buildAxisRows(rows, 0) }));
        setWfMarkersMap(prev => ({ ...prev, 0: buildWfMarkers(rows, point.name) }));
      }).catch(() => {});
  }, [point.machineId, point.name, accelerationTimeRange]);

  useEffect(() => {
    if (!point.machineId) return;
    const days = timeRangeToDays(envelopeTimeRange);
    apiFetch<any[]>(`/machines/${point.machineId}/trend?unit_code=6&days=${days}`)
      .then(rows => {
        setTrendData(prev => ({ ...prev, 6: buildAxisRows(rows, 6) }));
        setWfMarkersMap(prev => ({ ...prev, 6: buildWfMarkers(rows, point.name) }));
      }).catch(() => {});
  }, [point.machineId, point.name, envelopeTimeRange]);

  useEffect(() => {
    if (!point.machineId) return;
    const days = timeRangeToDays(temperatureTimeRange);
    apiFetch<any[]>(`/machines/${point.machineId}/sensor-health?days=${days}`)
      .then(rows => {
        const mine = (rows ?? []).filter(r => r.point_name === point.name && r.sensor_temperature != null);
        const buckets = new Map<string, number[]>();
        for (const r of mine) {
          const day = (r.polled_at || '').substring(0, 10);
          if (!buckets.has(day)) buckets.set(day, []);
          buckets.get(day)!.push(r.sensor_temperature);
        }
        const result = Array.from(buckets.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([day, vals]) => ({
            time: day,
            temp: parseFloat((vals.reduce((s, v) => s + v, 0) / vals.length * 9 / 5 + 32).toFixed(1)),
          }));
        setSensorTempData(result);
      })
      .catch(() => setSensorTempData([]));
  }, [point.machineId, point.name, temperatureTimeRange]);

  // Memoized raw (real data or fallback) and filtered arrays
  const rawVel   = useMemo(() => trendData[2].length ? trendData[2] : generatePointHistoricalData(point.velocity,     velocityTimeRange),     [trendData[2],     velocityTimeRange]);
  const rawAccel = useMemo(() => trendData[0].length ? trendData[0] : generatePointHistoricalData(point.acceleration, accelerationTimeRange), [trendData[0], accelerationTimeRange]);
  const rawEnv   = useMemo(() => trendData[6].length ? trendData[6] : generatePointHistoricalData(point.envelope,     envelopeTimeRange),     [trendData[6],     envelopeTimeRange]);
  const filteredVel   = useMemo(() => applyFloor(rawVel,       velFloor),   [rawVel,       velFloor]);
  const filteredAccel = useMemo(() => applyFloor(rawAccel,   accelFloor),   [rawAccel,   accelFloor]);
  const filteredEnv   = useMemo(() => applyFloor(rawEnv,       envFloor),   [rawEnv,       envFloor]);
  const filteredTemp  = useMemo(() => applyFloor(sensorTempData, tempFloor), [sensorTempData, tempFloor]);

  const UNIT_CODE: Record<string, number> = { velocity: 2, acceleration: 0, envelope: 6, temperature: 20 };

  const saveThresholdOverride = (metric: string, level: 'yellow' | 'red', value: number) => {
    if (!point.axisIds?.length) return;
    const unitCode = UNIT_CODE[metric];
    if (unitCode == null) return;
    const moderate = level === 'yellow' ? value : value / 1.5;
    const severe   = moderate * 1.5;
    for (const axisId of point.axisIds) {
      apiFetch(`/alarm-manager/${axisId}/${unitCode}`, {
        method: 'PATCH',
        body: JSON.stringify({ moderate_override: moderate, severe_override: severe }),
      }).catch(() => {});
    }
  };

  const handleThresholdChange = (metric: string, level: 'yellow' | 'red', value: number) => {
    let updatedThresholds;
    if (metric === 'velocity') {
      updatedThresholds = { ...velocityThresholds };
      updatedThresholds[level] = value;
      setVelocityThresholds(updatedThresholds);
      setVelocityDirty(true);
      if (onThresholdsUpdate) onThresholdsUpdate(point.id, 'velocity', updatedThresholds);
    } else if (metric === 'acceleration') {
      updatedThresholds = { ...accelerationThresholds };
      updatedThresholds[level] = value;
      setAccelerationThresholds(updatedThresholds);
      setAccelerationDirty(true);
      if (onThresholdsUpdate) onThresholdsUpdate(point.id, 'acceleration', updatedThresholds);
    } else if (metric === 'envelope') {
      updatedThresholds = { ...envelopeThresholds };
      updatedThresholds[level] = value;
      setEnvelopeThresholds(updatedThresholds);
      setEnvelopeDirty(true);
      if (onThresholdsUpdate) onThresholdsUpdate(point.id, 'envelope', updatedThresholds);
    } else if (metric === 'temperature') {
      updatedThresholds = { ...temperatureThresholds };
      updatedThresholds[level] = value;
      setTemperatureThresholds(updatedThresholds);
      if (onThresholdsUpdate) onThresholdsUpdate(point.id, 'temperature', updatedThresholds);
    }
  };

  const handleSave = (metric: string) => {
    const threshMap: Record<string, Thresholds> = {
      velocity: velocityThresholds,
      acceleration: accelerationThresholds,
      envelope: envelopeThresholds,
    };
    const t = threshMap[metric];
    if (!t) return;
    setSavingMetric(metric);
    saveThresholdOverride(metric, 'yellow', t.yellow);
    setTimeout(() => {
      if (metric === 'velocity') setVelocityDirty(false);
      else if (metric === 'acceleration') setAccelerationDirty(false);
      else if (metric === 'envelope') setEnvelopeDirty(false);
      setSavingMetric(null);
    }, 600);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'low': return 'bg-green-500';
      case 'medium': return 'bg-yellow-500';
      case 'high': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  // Render filled dot (axis color) when waveform available, small semi-transparent dot otherwise
  const makeDot = (unitCode: number, axisColor: string) => (p: any) => {
    const { cx, cy, payload, index } = p;
    if (!cx || !cy) return <circle key={index} r={0} />;
    const wf = wfMarkersMap[unitCode]?.get(payload.time);
    if (wf) return <circle key={index} cx={cx} cy={cy} r={4} fill={axisColor} stroke="white" strokeWidth={1.5} />;
    return <circle key={index} cx={cx} cy={cy} r={2} fill={axisColor} fillOpacity={0.5} />;
  };

  // V/H/A series toggle state — pre-select a single axis when coming from sidebar axis click
  const [hiddenAxes, setHiddenAxes] = useState<Set<string>>(() => {
    if (!initialAxisNum) return new Set<string>();
    const shown = initialAxisNum === 1 ? 'H' : initialAxisNum === 2 ? 'V' : 'A';
    return new Set(['H', 'V', 'A'].filter(a => a !== shown));
  });
  const toggleAxis = (axis: string) => setHiddenAxes(prev => {
    const next = new Set(prev);
    if (next.has(axis)) next.delete(axis); else next.add(axis);
    return next;
  });

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors group">
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform" />
          <span className="text-sm font-medium">Back</span>
        </button>
        <div className="flex items-center gap-3">
          <h2 className="text-gray-900 text-xl font-semibold">{point.name} — Detailed Analysis</h2>
          <div className={`w-3 h-3 rounded-full ${getStatusColor(point.status)}`}></div>
        </div>
      </div>

      <div className="space-y-6">
          {/* Current Values */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
              <div className="text-gray-600 mb-2">Velocity</div>
              <div className="text-gray-900">V: {point.velocity.V.toFixed(2)} in/s</div>
              <div className="text-gray-900">H: {point.velocity.H.toFixed(2)} in/s</div>
              <div className="text-gray-900">A: {point.velocity.A.toFixed(2)} in/s</div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
              <div className="text-gray-600 mb-2">Acceleration</div>
              <div className="text-gray-900">V: {point.acceleration.V.toFixed(2)} G&apos;s</div>
              <div className="text-gray-900">H: {point.acceleration.H.toFixed(2)} G&apos;s</div>
              <div className="text-gray-900">A: {point.acceleration.A.toFixed(2)} G&apos;s</div>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
              <div className="text-gray-600 mb-2">Envelope</div>
              <div className="text-gray-900">V: {point.envelope.V.toFixed(2)} G&apos;s</div>
              <div className="text-gray-900">H: {point.envelope.H.toFixed(2)} G&apos;s</div>
              <div className="text-gray-900">A: {point.envelope.A.toFixed(2)} G&apos;s</div>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border border-orange-200">
              <div className="text-gray-600 mb-2">Temperature</div>
              <div className="text-gray-900">V: {point.temperature.V.toFixed(0)}&deg;F</div>
              <div className="text-gray-900">H: {point.temperature.H.toFixed(0)}&deg;F</div>
              <div className="text-gray-900">A: {point.temperature.A.toFixed(0)}&deg;F</div>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Velocity Chart */}
            <ChartContainer title="Velocity (in/s)">
              {(() => {
                const yMax = calcYMax(filteredVel, velocityThresholds.yellow);
                return (
                  <div className="flex gap-2 items-start">
                    <FloorSlider floor={velFloor} setFloor={setVelFloor} yMax={yMax} />
                    <div className="flex-1 min-w-0" style={{ cursor: wfHover?.unitCode === 2 ? 'pointer' : 'default' }}>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={filteredVel} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          onMouseMove={(s: any) => {
                            if (s.isTooltipActive && s.activeTooltipIndex !== undefined) {
                              const nw = findNearestWfPt(filteredVel, s.activeTooltipIndex, wfMarkersMap[2]);
                              setWfHover(nw ? { unitCode: 2, ...nw } : null);
                            } else { setWfHover(null); }
                          }}
                          onMouseLeave={() => setWfHover(null)}
                          onClick={() => {
                            const h = wfHoverRef.current;
                            if (h?.unitCode === 2) setWaveformJumpTo({ measured_at: h.measured_at, point_name: point.name });
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                          <YAxis domain={[0, yMax]} tickFormatter={v => v.toFixed(3)} />
                          <Tooltip formatter={(v: number) => [typeof v === 'number' ? v.toFixed(4) : v, '']} />
                          {velocityThresholds.yellow > 0 && <ReferenceLine y={velocityThresholds.yellow} stroke="#F59E0B" strokeWidth={2} strokeDasharray="6 3" label={{ value: `Mod: ${velocityThresholds.yellow.toFixed(3)}`, position: 'insideTopRight', fill: '#f59e0b', fontSize: 10 }} />}
                          {velocityThresholds.red > 0 && <ReferenceLine y={velocityThresholds.red} stroke="#EF4444" strokeWidth={2} strokeDasharray="6 3" label={{ value: `Sev: ${velocityThresholds.red.toFixed(3)}`, position: 'insideTopRight', fill: '#ef4444', fontSize: 10 }} />}
                          {velFloor > 0 && <ReferenceLine y={velFloor} stroke="#64748b" strokeDasharray="4 4" strokeWidth={1} label={{ value: `Floor: ${velFloor.toFixed(3)}`, position: 'insideTopLeft', fill: '#64748b', fontSize: 10 }} />}
                          {wfHover?.unitCode === 2 && <ReferenceLine x={wfHover.time} stroke="#06b6d4" strokeWidth={2} label={{ value: fmtWfLabel(wfHover.measured_at), position: 'insideBottomLeft', fill: '#06b6d4', fontSize: 9, fontWeight: 600 }} />}
                          <Line type="monotone" dataKey="V" stroke={AXIS_COLORS.V} isAnimationActive={false} connectNulls={true} dot={makeDot(2, AXIS_COLORS.V)} hide={hiddenAxes.has('V')} />
                          <Line type="monotone" dataKey="H" stroke={AXIS_COLORS.H} isAnimationActive={false} connectNulls={true} dot={makeDot(2, AXIS_COLORS.H)} hide={hiddenAxes.has('H')} />
                          <Line type="monotone" dataKey="A" stroke={AXIS_COLORS.A} isAnimationActive={false} connectNulls={true} dot={makeDot(2, AXIS_COLORS.A)} hide={hiddenAxes.has('A')} />
                        </LineChart>
                      </ResponsiveContainer>
                      <div className="flex justify-center gap-2 pt-1">
                        {(['V', 'H', 'A'] as const).map(axis => (
                          <button key={axis} onClick={() => toggleAxis(axis)}
                            className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border transition-all select-none"
                            style={{ borderColor: AXIS_COLORS[axis], background: 'white', opacity: hiddenAxes.has(axis) ? 0.4 : 1 }}>
                            <span className="w-2 h-2 rounded-full" style={{ background: AXIS_COLORS[axis] }} />{axis}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
              <div className="mt-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Time Range:</span>
                  <span className="text-gray-900">{getTimeRangeLabel(velocityTimeRange)}</span>
                </div>
                <input type="range" min="0" max="3" step="1" value={velocityTimeRange}
                  onChange={(e) => setVelocityTimeRange(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider" />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-gray-500">Hours</span>
                  <span className="text-xs text-gray-500">Days</span>
                  <span className="text-xs text-gray-500">Weeks</span>
                  <span className="text-xs text-gray-500">Months</span>
                </div>
              </div>
              <div className="mt-4 space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Alarm Threshold (3σ)</span>
                    <span className="text-gray-900">{velocityThresholds.yellow.toFixed(3)}</span>
                  </div>
                  <input type="range" min="0" max={Math.max(velocityThresholds.red * 2, 1)} step="0.001"
                    value={velocityThresholds.yellow}
                    onChange={(e) => handleThresholdChange('velocity', 'yellow', Number(e.target.value))}
                    className="w-full h-2 bg-yellow-200 rounded-lg appearance-none cursor-grab active:cursor-grabbing slider" />
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Critical Threshold (3σ ×1.5)</span>
                    <span className="text-gray-900">{velocityThresholds.red.toFixed(3)}</span>
                  </div>
                  <input type="range" min="0" max={Math.max(velocityThresholds.red * 2, 1)} step="0.001"
                    value={velocityThresholds.red}
                    onChange={(e) => handleThresholdChange('velocity', 'red', Number(e.target.value))}
                    className="w-full h-2 bg-red-200 rounded-lg appearance-none cursor-grab active:cursor-grabbing slider" />
                </div>
                {velocityDirty && (
                  <button onClick={() => handleSave('velocity')} className="w-full py-2 rounded-lg text-sm font-medium transition-colors bg-blue-600 hover:bg-blue-700 text-white">
                    {savingMetric === 'velocity' ? 'Saving…' : 'Save Thresholds'}
                  </button>
                )}
              </div>
            </ChartContainer>

            {/* Acceleration Chart */}
            <ChartContainer title="Acceleration (G's)">
              {(() => {
                const yMax = calcYMax(filteredAccel, accelerationThresholds.yellow);
                return (
                  <div className="flex gap-2 items-start">
                    <FloorSlider floor={accelFloor} setFloor={setAccelFloor} yMax={yMax} />
                    <div className="flex-1 min-w-0" style={{ cursor: wfHover?.unitCode === 0 ? 'pointer' : 'default' }}>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={filteredAccel} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          onMouseMove={(s: any) => {
                            if (s.isTooltipActive && s.activeTooltipIndex !== undefined) {
                              const nw = findNearestWfPt(filteredAccel, s.activeTooltipIndex, wfMarkersMap[0]);
                              setWfHover(nw ? { unitCode: 0, ...nw } : null);
                            } else { setWfHover(null); }
                          }}
                          onMouseLeave={() => setWfHover(null)}
                          onClick={() => {
                            const h = wfHoverRef.current;
                            if (h?.unitCode === 0) setWaveformJumpTo({ measured_at: h.measured_at, point_name: point.name });
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                          <YAxis domain={[0, yMax]} tickFormatter={v => v.toFixed(3)} />
                          <Tooltip formatter={(v: number) => [typeof v === 'number' ? v.toFixed(3) : v, '']} />
                          {accelerationThresholds.yellow > 0 && <ReferenceLine y={accelerationThresholds.yellow} stroke="#F59E0B" strokeWidth={2} strokeDasharray="6 3" label={{ value: `Mod: ${accelerationThresholds.yellow.toFixed(3)}`, position: 'insideTopRight', fill: '#f59e0b', fontSize: 10 }} />}
                          {accelerationThresholds.red > 0 && <ReferenceLine y={accelerationThresholds.red} stroke="#EF4444" strokeWidth={2} strokeDasharray="6 3" label={{ value: `Sev: ${accelerationThresholds.red.toFixed(3)}`, position: 'insideTopRight', fill: '#ef4444', fontSize: 10 }} />}
                          {accelFloor > 0 && <ReferenceLine y={accelFloor} stroke="#64748b" strokeDasharray="4 4" strokeWidth={1} label={{ value: `Floor: ${accelFloor.toFixed(3)}`, position: 'insideTopLeft', fill: '#64748b', fontSize: 10 }} />}
                          {wfHover?.unitCode === 0 && <ReferenceLine x={wfHover.time} stroke="#06b6d4" strokeWidth={2} label={{ value: fmtWfLabel(wfHover.measured_at), position: 'insideBottomLeft', fill: '#06b6d4', fontSize: 9, fontWeight: 600 }} />}
                          <Line type="monotone" dataKey="V" stroke={AXIS_COLORS.V} isAnimationActive={false} connectNulls={true} dot={makeDot(0, AXIS_COLORS.V)} hide={hiddenAxes.has('V')} />
                          <Line type="monotone" dataKey="H" stroke={AXIS_COLORS.H} isAnimationActive={false} connectNulls={true} dot={makeDot(0, AXIS_COLORS.H)} hide={hiddenAxes.has('H')} />
                          <Line type="monotone" dataKey="A" stroke={AXIS_COLORS.A} isAnimationActive={false} connectNulls={true} dot={makeDot(0, AXIS_COLORS.A)} hide={hiddenAxes.has('A')} />
                        </LineChart>
                      </ResponsiveContainer>
                      <div className="flex justify-center gap-2 pt-1">
                        {(['V', 'H', 'A'] as const).map(axis => (
                          <button key={axis} onClick={() => toggleAxis(axis)}
                            className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border transition-all select-none"
                            style={{ borderColor: AXIS_COLORS[axis], background: 'white', opacity: hiddenAxes.has(axis) ? 0.4 : 1 }}>
                            <span className="w-2 h-2 rounded-full" style={{ background: AXIS_COLORS[axis] }} />{axis}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
              <div className="mt-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Time Range:</span>
                  <span className="text-gray-900">{getTimeRangeLabel(accelerationTimeRange)}</span>
                </div>
                <input type="range" min="0" max="3" step="1" value={accelerationTimeRange}
                  onChange={(e) => setAccelerationTimeRange(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider" />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-gray-500">Hours</span>
                  <span className="text-xs text-gray-500">Days</span>
                  <span className="text-xs text-gray-500">Weeks</span>
                  <span className="text-xs text-gray-500">Months</span>
                </div>
              </div>
              <div className="mt-4 space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Alarm Threshold (3σ)</span>
                    <span className="text-gray-900">{accelerationThresholds.yellow.toFixed(4)}</span>
                  </div>
                  <input type="range" min="0" max={Math.max(accelerationThresholds.red * 2, 1)} step="0.0001"
                    value={accelerationThresholds.yellow}
                    onChange={(e) => handleThresholdChange('acceleration', 'yellow', Number(e.target.value))}
                    className="w-full h-2 bg-yellow-200 rounded-lg appearance-none cursor-grab active:cursor-grabbing slider" />
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Critical Threshold (3σ ×1.5)</span>
                    <span className="text-gray-900">{accelerationThresholds.red.toFixed(4)}</span>
                  </div>
                  <input type="range" min="0" max={Math.max(accelerationThresholds.red * 2, 1)} step="0.0001"
                    value={accelerationThresholds.red}
                    onChange={(e) => handleThresholdChange('acceleration', 'red', Number(e.target.value))}
                    className="w-full h-2 bg-red-200 rounded-lg appearance-none cursor-grab active:cursor-grabbing slider" />
                </div>
                {accelerationDirty && (
                  <button onClick={() => handleSave('acceleration')} className="w-full py-2 rounded-lg text-sm font-medium transition-colors bg-blue-600 hover:bg-blue-700 text-white">
                    {savingMetric === 'acceleration' ? 'Saving…' : 'Save Thresholds'}
                  </button>
                )}
              </div>
            </ChartContainer>

            {/* Envelope Chart */}
            <ChartContainer title="Envelope (G's)">
              {(() => {
                const yMax = calcYMax(filteredEnv, envelopeThresholds.yellow);
                return (
                  <div className="flex gap-2 items-start">
                    <FloorSlider floor={envFloor} setFloor={setEnvFloor} yMax={yMax} />
                    <div className="flex-1 min-w-0" style={{ cursor: wfHover?.unitCode === 6 ? 'pointer' : 'default' }}>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={filteredEnv} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          onMouseMove={(s: any) => {
                            if (s.isTooltipActive && s.activeTooltipIndex !== undefined) {
                              const nw = findNearestWfPt(filteredEnv, s.activeTooltipIndex, wfMarkersMap[6]);
                              setWfHover(nw ? { unitCode: 6, ...nw } : null);
                            } else { setWfHover(null); }
                          }}
                          onMouseLeave={() => setWfHover(null)}
                          onClick={() => {
                            const h = wfHoverRef.current;
                            if (h?.unitCode === 6) setWaveformJumpTo({ measured_at: h.measured_at, point_name: point.name });
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                          <YAxis domain={[0, yMax]} tickFormatter={v => v.toFixed(3)} />
                          <Tooltip formatter={(v: number) => [typeof v === 'number' ? v.toFixed(3) : v, '']} />
                          {envelopeThresholds.yellow > 0 && <ReferenceLine y={envelopeThresholds.yellow} stroke="#F59E0B" strokeWidth={2} strokeDasharray="6 3" label={{ value: `Mod: ${envelopeThresholds.yellow.toFixed(4)}`, position: 'insideTopRight', fill: '#f59e0b', fontSize: 10 }} />}
                          {envelopeThresholds.red > 0 && <ReferenceLine y={envelopeThresholds.red} stroke="#EF4444" strokeWidth={2} strokeDasharray="6 3" label={{ value: `Sev: ${envelopeThresholds.red.toFixed(4)}`, position: 'insideTopRight', fill: '#ef4444', fontSize: 10 }} />}
                          {envFloor > 0 && <ReferenceLine y={envFloor} stroke="#64748b" strokeDasharray="4 4" strokeWidth={1} label={{ value: `Floor: ${envFloor.toFixed(3)}`, position: 'insideTopLeft', fill: '#64748b', fontSize: 10 }} />}
                          {wfHover?.unitCode === 6 && <ReferenceLine x={wfHover.time} stroke="#06b6d4" strokeWidth={2} label={{ value: fmtWfLabel(wfHover.measured_at), position: 'insideBottomLeft', fill: '#06b6d4', fontSize: 9, fontWeight: 600 }} />}
                          <Line type="monotone" dataKey="V" stroke={AXIS_COLORS.V} isAnimationActive={false} connectNulls={true} dot={makeDot(6, AXIS_COLORS.V)} hide={hiddenAxes.has('V')} />
                          <Line type="monotone" dataKey="H" stroke={AXIS_COLORS.H} isAnimationActive={false} connectNulls={true} dot={makeDot(6, AXIS_COLORS.H)} hide={hiddenAxes.has('H')} />
                          <Line type="monotone" dataKey="A" stroke={AXIS_COLORS.A} isAnimationActive={false} connectNulls={true} dot={makeDot(6, AXIS_COLORS.A)} hide={hiddenAxes.has('A')} />
                        </LineChart>
                      </ResponsiveContainer>
                      <div className="flex justify-center gap-2 pt-1">
                        {(['V', 'H', 'A'] as const).map(axis => (
                          <button key={axis} onClick={() => toggleAxis(axis)}
                            className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border transition-all select-none"
                            style={{ borderColor: AXIS_COLORS[axis], background: 'white', opacity: hiddenAxes.has(axis) ? 0.4 : 1 }}>
                            <span className="w-2 h-2 rounded-full" style={{ background: AXIS_COLORS[axis] }} />{axis}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
              <div className="mt-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Time Range:</span>
                  <span className="text-gray-900">{getTimeRangeLabel(envelopeTimeRange)}</span>
                </div>
                <input type="range" min="0" max="3" step="1" value={envelopeTimeRange}
                  onChange={(e) => setEnvelopeTimeRange(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider" />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-gray-500">Hours</span>
                  <span className="text-xs text-gray-500">Days</span>
                  <span className="text-xs text-gray-500">Weeks</span>
                  <span className="text-xs text-gray-500">Months</span>
                </div>
              </div>
              <div className="mt-4 space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Alarm Threshold (3σ)</span>
                    <span className="text-gray-900">{envelopeThresholds.yellow.toFixed(4)}</span>
                  </div>
                  <input type="range" min="0" max={Math.max(envelopeThresholds.red * 2, 1)} step="0.0001"
                    value={envelopeThresholds.yellow}
                    onChange={(e) => handleThresholdChange('envelope', 'yellow', Number(e.target.value))}
                    className="w-full h-2 bg-yellow-200 rounded-lg appearance-none cursor-grab active:cursor-grabbing slider" />
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Critical Threshold (3σ ×1.5)</span>
                    <span className="text-gray-900">{envelopeThresholds.red.toFixed(4)}</span>
                  </div>
                  <input type="range" min="0" max={Math.max(envelopeThresholds.red * 2, 1)} step="0.0001"
                    value={envelopeThresholds.red}
                    onChange={(e) => handleThresholdChange('envelope', 'red', Number(e.target.value))}
                    className="w-full h-2 bg-red-200 rounded-lg appearance-none cursor-grab active:cursor-grabbing slider" />
                </div>
                {envelopeDirty && (
                  <button onClick={() => handleSave('envelope')} className="w-full py-2 rounded-lg text-sm font-medium transition-colors bg-blue-600 hover:bg-blue-700 text-white">
                    {savingMetric === 'envelope' ? 'Saving…' : 'Save Thresholds'}
                  </button>
                )}
              </div>
            </ChartContainer>

            {/* Temperature Chart */}
            <ChartContainer title="Temperature (°F)">
              {sensorTempData.length === 0 ? (
                <div className="flex items-center justify-center h-[300px] text-gray-400 text-sm">No sensor temperature data available</div>
              ) : (() => {
                const yMax = calcYMax(filteredTemp);
                return (
                  <div className="flex gap-2 items-start">
                    <FloorSlider floor={tempFloor} setFloor={setTempFloor} yMax={yMax} />
                    <div className="flex-1 min-w-0">
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={filteredTemp} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="time" tick={{ fontSize: 11 }} />
                          <YAxis unit="°F" domain={[0, yMax]} />
                          <Tooltip formatter={(v: number) => [`${v}°F`, 'Sensor Temp']} />
                          {tempFloor > 0 && <ReferenceLine y={tempFloor} stroke="#64748b" strokeDasharray="4 4" strokeWidth={1} label={{ value: `Floor: ${tempFloor.toFixed(1)}`, position: 'insideTopLeft', fill: '#64748b', fontSize: 10 }} />}
                          <Line type="monotone" dataKey="temp" stroke="#F97316" strokeWidth={2} isAnimationActive={false} connectNulls={true} dot={{ r: 2 }} name="Sensor Temp" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                );
              })()}
              <div className="mt-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Time Range:</span>
                  <span className="text-gray-900">{getTimeRangeLabel(temperatureTimeRange)}</span>
                </div>
                <input type="range" min="0" max="3" step="1" value={temperatureTimeRange}
                  onChange={(e) => setTemperatureTimeRange(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider" />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-gray-500">Hours</span>
                  <span className="text-xs text-gray-500">Days</span>
                  <span className="text-xs text-gray-500">Weeks</span>
                  <span className="text-xs text-gray-500">Months</span>
                </div>
              </div>
            </ChartContainer>
          </div>

          {/* Waveform Viewer */}
          {point.machineId && (
            <WaveformViewer
              machineId={point.machineId}
              jumpTo={waveformJumpTo}
              operatingRpm={null}
              isVfd={false}
            />
          )}
      </div>
    </div>
  );
}
